import React, { useEffect, useState } from "react";
import {
  Avatar,
  Card,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import AppDialog, {
  CustomAppDialog,
  CustomDialogCloseButton,
} from "../../components/dialog/Dialog";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog } from "../../components/dialog/dialogSlice";
import {
  deleteToDoAttachment,
  getAllToDos,
  getToDoByID,
  insertToDoAttachment,
  updateTaskStatus,
  updateToDo,
} from "./services/toDo.services";
import {
  clearAttachementID,
  clearToDoDetails,
  clearToDoGUID,
  closeAttachementDeleteOpen,
  closeTimeDialogOpen,
  openAttachementDeleteOpen,
  openTimeDialogOpen,
  setAttachementID,
} from "./toDoSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import FormTimePicker from "../../components/formFields/FormTimeField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormFileUpload from "../../components/formFields/FormFileUpload";
import IconButton from "../../components/common/IconButton";
import { MdDelete } from "react-icons/md";
import { FaEye } from "react-icons/fa";
// import { LoadingPanel } from "../../components/layout/Loading";
import { RxCross1 } from "react-icons/rx";
import { IoCheckmarkDoneSharp, IoMoon } from "react-icons/io5";
import { ImCalendar } from "react-icons/im";
import { BsCupFill } from "react-icons/bs";
import { IoIosPartlySunny } from "react-icons/io";
import { PiSunHorizonFill } from "react-icons/pi";
import { GrPowerReset } from "react-icons/gr";

const CreateToDo: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.todo.loading);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const ToDoGUID = useAppSelector((state) => state.todo.ToDoGUID);
  const ToDoDetail = useAppSelector((state) => state.todo.ToDoDetail);

  const [formKey, setFormkey] = useState(1);

  useEffect(() => {
    if (ToDoGUID) {
      dispatch(getToDoByID(ToDoGUID));
    }
  }, [ToDoGUID]);

  useEffect(() => {
    setFormkey(formKey + 1);
  }, [ToDoDetail]);

  useEffect(() => {
    return () => {
      dispatch(clearToDoDetails());
      dispatch(clearToDoGUID());
      dispatch(closeDialog());
    };
  }, []);

  const handleCloseDialog = () => {
    dispatch(clearToDoDetails());
    dispatch(clearToDoGUID());
    dispatch(closeDialog());
  };

  const handleTimeChange = () => {
    dispatch(openTimeDialogOpen());
  };

  const handleOpenAttachmentDeleteDialog = (ID: number) => {
    dispatch(openAttachementDeleteOpen());
    dispatch(setAttachementID(ID));
  };
  const handleTaskStatus = async (TaskStatus: string, ToDoGUID: string) => {
    const payload = {
      TitleGUID: ToDoGUID ? ToDoGUID : "",
      ToDoStatus: TaskStatus === "DONE" ? true : false,
    };
    if (TaskStatus === "DONE") {
      const res = await dispatch(updateTaskStatus(payload));
      if (res.payload.Status === 200) {
        dispatch(closeDialog());
        dispatch(clearToDoDetails());
        dispatch(clearToDoGUID());
        dispatch(getAllToDos());
      }
    } else if (TaskStatus === "RESET") {
      const res = await dispatch(updateTaskStatus(payload));
      if (res.payload.Status === 200) {
        dispatch(closeDialog());
        dispatch(clearToDoDetails());
        dispatch(clearToDoGUID());
        dispatch(getAllToDos());
      }
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        TitleGUID: ToDoGUID ? ToDoGUID : "",
        Title: values?.Title ? values?.Title : "",
        Reminder:
          values?.Reminder && values?.ReminderTime
            ? `${moment(values?.Reminder).format("YYYY-MM-DD")}T${moment(
                values?.ReminderTime
              ).format("HH:mm:ss")}`
            : "",
        Note: values?.Note ? values?.Note : "",
        IsActive: true,
      };

      const response = await dispatch(updateToDo(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        if (values.ToDoAttachment?.length > 0) {
          const files = new FormData();

          files.append("ToDoID", `${values?.ID}`);
          let AttachmentName: string[] = [];
          values?.ToDoAttachment?.forEach((doc: any) => {
            const binaryDoc = doc.getRawFile();
            AttachmentName.push(binaryDoc.name);
            files.append("ToDoAttachment", binaryDoc);
          });
          files.append("AttachmentName", AttachmentName.join(", "));

          const response = await dispatch(insertToDoAttachment(files));
          if (response.payload.Status === 200) {
            dispatch(closeDialog());
            dispatch(clearToDoDetails());
            dispatch(clearToDoGUID());
            dispatch(getAllToDos());
          } else {
            dispatch(closeDialog());
            dispatch(clearToDoDetails());
            dispatch(clearToDoGUID());
            dispatch(getAllToDos());
          }
        } else {
          dispatch(closeDialog());
          dispatch(clearToDoDetails());
          dispatch(clearToDoGUID());
          dispatch(getAllToDos());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      <DeleteAttachmentDialog />
      {dialogName === "updateToDo" && (
        <AppDialog>
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={ToDoDetail}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement style={{ width: "100%", minWidth: 500 }}>
                <Typography.h3 className="fw-bold">
                  {"Update Task"}
                </Typography.h3>
                <hr className="mb-0" />
                <GridLayout>
                  <GridLayoutItem>
                    <Field
                      id="Title"
                      name="Title"
                      label="Task Title"
                      placeholder="Add a task..."
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem className="d-flex gap-2">
                    <Field
                      wrapperStyle={{
                        width: "100%",
                      }}
                      name="Reminder"
                      label="Reminder Date"
                      component={FormDatePicker}
                      minDate={moment().toDate()}
                      disabled={
                        !formRenderProps.valueGetter("isDateTimeEditable")
                      }
                      placeholder="Date"
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      wrapperStyle={{
                        width: "100%",
                      }}
                      name="ReminderTime"
                      label="Reminder Time"
                      min={
                        moment(
                          formRenderProps.valueGetter("ReminderDate")
                        ).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD")
                          ? moment().toDate()
                          : undefined
                      }
                      disabled={
                        !formRenderProps.valueGetter("isDateTimeEditable")
                      }
                      component={FormTimePicker}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <div>
                      <Button
                        type="button"
                        themeColor={"primary"}
                        fillMode={"solid"}
                        style={{ marginTop: 34 }}
                        onClick={() => handleTimeChange()}
                      >
                        Change
                      </Button>
                    </div>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id="Note"
                      name="Note"
                      label="Note"
                      placeholder="Add Notes..."
                      rows={2}
                      component={FormTextArea}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="ToDoAttachment"
                      type="file"
                      filetype="file"
                      multiple
                      label="Attachments"
                      component={FormFileUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      maxHeight: "92px",
                      overflowY: "scroll",
                      scrollbarWidth: "thin",
                    }}
                  >
                    {ToDoDetail?.ToDoAttachmentList &&
                      ToDoDetail?.ToDoAttachmentList?.length > 0 &&
                      ToDoDetail?.ToDoAttachmentList?.map((attachment: any) => {
                        return (
                          <Card style={{ marginTop: 10, padding: 10 }}>
                            <div className="d-flex gap-2">
                              <Avatar
                                style={{
                                  width: 60,
                                  height: 60,
                                  flexBasis: 60,
                                  backgroundColor: "transparent",
                                }}
                                rounded={"full"}
                                type={"image"}
                              >
                                <img
                                  style={{ width: 60, height: 60 }}
                                  src={attachment?.AttachmentURL}
                                  alt={"Attachment"}
                                />
                              </Avatar>
                              <div>
                                <p className="mb-1">
                                  {attachment?.AttachmentName}
                                </p>
                                <div className="d-flex gap-2">
                                  <IconButton
                                    type="button"
                                    fillMode="solid"
                                    size="small"
                                    title="View"
                                    onClick={() =>
                                      window.open(attachment?.AttachmentURL)
                                    }
                                  >
                                    <FaEye
                                      className="absolute-position"
                                      style={{ fontSize: "20px" }}
                                    />
                                  </IconButton>
                                  <IconButton
                                    type="button"
                                    fillMode="solid"
                                    size="small"
                                    title="Delete"
                                    themeColor="error"
                                    onClick={() =>
                                      handleOpenAttachmentDeleteDialog(
                                        attachment?.ID
                                      )
                                    }
                                  >
                                    <MdDelete
                                      className="absolute-position"
                                      style={{ fontSize: "20px" }}
                                    />
                                  </IconButton>
                                </div>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 20,
                    }}
                  >
                    <div className="d-flex gap-1">
                      <Button
                        type="button"
                        themeColor={"primary"}
                        fillMode={ToDoDetail?.ToDoStatus ? "solid" : "outline"}
                        style={{ marginRight: 10 }}
                        onClick={() =>
                          !ToDoDetail?.ToDoStatus &&
                          handleTaskStatus("DONE", ToDoGUID)
                        }
                      >
                        {ToDoDetail?.ToDoStatus && (
                          <IoCheckmarkDoneSharp
                            style={{ color: "aqua", fontSize: 20 }}
                          />
                        )}{" "}
                        Mark As Complete
                      </Button>
                      {ToDoDetail?.ToDoStatus && (
                        <IconButton
                          type="button"
                          fillMode="outline"
                          size="small"
                          title="Undo Task Status"
                          themeColor="error"
                          onClick={() => handleTaskStatus("RESET", ToDoGUID)}
                        >
                          <GrPowerReset
                            className="absolute-position"
                            style={{ fontSize: "20px" }}
                          />
                        </IconButton>
                      )}
                    </div>
                    <div className="d-flex align-items-end">
                      <Button
                        type="button"
                        themeColor={"primary"}
                        fillMode={"outline"}
                        style={{ marginRight: 10 }}
                        onClick={handleCloseDialog}
                      >
                        Cancel
                      </Button>
                      <ButtonWithLoading
                        label={"Update"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                      />
                    </div>
                  </GridLayoutItem>
                </GridLayout>
                <DateTimeDialog formRenderProps={formRenderProps} />
              </FormElement>
            )}
          />
        </AppDialog>
      )}
    </>
  );
};

const DateTimeDialog = ({
  formRenderProps,
}: {
  formRenderProps: FormRenderProps;
}) => {
  const dispatch = useAppDispatch();
  const timeDialogOpen = useAppSelector((state) => state.todo.timeDialogOpen);

  const handleChangeDateTime = (
    tabIndex: number,
    formRenderProps: FormRenderProps
  ) => {
    switch (tabIndex) {
      case 1:
        formRenderProps.onChange("isDateTimeEditable", {
          value: false,
        });
        formRenderProps.onChange("Reminder", {
          value: moment().toDate(),
        });
        formRenderProps.onChange("ReminderTime", {
          value: moment("15:00:00", "HH:mm:ss").toDate(),
        });
        break;
      case 2:
        formRenderProps.onChange("isDateTimeEditable", {
          value: false,
        });
        formRenderProps.onChange("Reminder", {
          value: moment().toDate(),
        });
        formRenderProps.onChange("ReminderTime", {
          value: moment("18:00:00", "HH:mm:ss").toDate(),
        });
        break;
      case 3:
        formRenderProps.onChange("isDateTimeEditable", {
          value: false,
        });
        formRenderProps.onChange("Reminder", {
          value: moment().add(1, "days").toDate(),
        });
        formRenderProps.onChange("ReminderTime", {
          value: moment("09:00:00", "HH:mm:ss").toDate(),
        });
        break;
      case 4:
        formRenderProps.onChange("isDateTimeEditable", {
          value: false,
        });
        formRenderProps.onChange("Reminder", {
          value: moment().add(1, "weeks").startOf("week").toDate(),
        });
        formRenderProps.onChange("ReminderTime", {
          value: moment("09:00:00", "HH:mm:ss").toDate(),
        });
        break;
      case 5:
        formRenderProps.onChange("isDateTimeEditable", {
          value: true,
        });
        break;

      default:
        break;
    }

    return dispatch(closeTimeDialogOpen());
  };

  return (
    <>
      <CustomAppDialog
        open={timeDialogOpen}
        closeDialogFunction={closeTimeDialogOpen}
      >
        <>
          <GridLayout style={{ minWidth: 250, maxWidth: 350 }}>
            <GridLayoutItem className="d-flex justify-content-between align-items-center mb-2">
              <Typography.h4 className="m-0">Add A Reminder</Typography.h4>
              <IconButton
                type="button"
                fillMode="flat"
                size="small"
                title="Delete"
                themeColor="error"
                onClick={() => dispatch(closeTimeDialogOpen())}
              >
                <RxCross1
                  className="absolute-position"
                  style={{ fontSize: "20px" }}
                />
              </IconButton>
            </GridLayoutItem>
            <GridLayoutItem>
              <hr className="mt-0" />
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                margin: "10px 0 20px",
              }}
            >
              {+moment().format("HH") <= 14 && (
                <div
                  className="date-time-div p-2"
                  onClick={() => handleChangeDateTime(1, formRenderProps)}
                >
                  <PiSunHorizonFill
                    style={{ fontSize: "20px" }}
                    className="mb-2"
                  />
                  <p className="mb-1">Later Today</p>
                  <p className="mb-1">03:00 PM</p>
                </div>
              )}
              {+moment().format("HH") <= 17 && (
                <div
                  className="date-time-div p-2"
                  onClick={() => handleChangeDateTime(2, formRenderProps)}
                >
                  <IoMoon style={{ fontSize: "20px" }} className="mb-2" />
                  <p className="mb-1">This Evening</p>
                  <p className="mb-1">06:00 PM</p>
                </div>
              )}
              <div
                className="date-time-div p-2"
                onClick={() => handleChangeDateTime(3, formRenderProps)}
              >
                <BsCupFill style={{ fontSize: "20px" }} className="mb-2" />
                <p className="mb-1">Tomorrow</p>
                <p className="mb-1">09:00 AM</p>
              </div>
              <div
                className="date-time-div p-2"
                onClick={() => handleChangeDateTime(4, formRenderProps)}
              >
                <IoIosPartlySunny
                  style={{ fontSize: "20px" }}
                  className="mb-2"
                />
                <p className="mb-1">Next Week</p>
                <p className="mb-1">09:00 AM</p>
              </div>
              <div
                className="date-time-div p-2"
                onClick={() => handleChangeDateTime(5, formRenderProps)}
              >
                <ImCalendar style={{ fontSize: "25px" }} className="mb-2" />
                <p className="mb-1">Custom</p>
              </div>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="primary"
                closeDialogFunction={closeTimeDialogOpen}
              />
              <Button
                fillMode={"solid"}
                themeColor={"primary"}
                // onClick={() => handleDeleteAction(AttachementID)}
                type="button"
              >
                Save
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>
    </>
  );
};
const DeleteAttachmentDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const attachementDeleteOpen = useAppSelector(
    (state) => state.todo.attachementDeleteOpen
  );
  const AttachementID = useAppSelector((state) => state.todo.AttachementID);
  const ToDoGUID = useAppSelector((state) => state.todo.ToDoGUID);

  const handleDeleteAction = async (AttachementID: number | null) => {
    if (AttachementID) {
      const response = await dispatch(deleteToDoAttachment(AttachementID));
      if (response.payload.Status === 200) {
        if (ToDoGUID) {
          dispatch(getToDoByID(ToDoGUID));
        }
        dispatch(closeAttachementDeleteOpen());
        dispatch(clearAttachementID());
      } else {
        dispatch(closeAttachementDeleteOpen());
        dispatch(clearAttachementID());
      }
    }
  };

  return (
    <>
      <CustomAppDialog
        open={attachementDeleteOpen}
        closeDialogFunction={closeAttachementDeleteOpen}
      >
        <>
          <GridLayout style={{ minWidth: 400 }}>
            <GridLayoutItem className="d-flex justify-content-between align-items-center mb-2">
              <Typography.h4 className="m-0">
                {"Delete Attachment"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode="flat"
                size="small"
                title="Delete"
                themeColor="error"
                onClick={() => dispatch(closeAttachementDeleteOpen())}
              >
                <RxCross1
                  className="absolute-position"
                  style={{ fontSize: "20px" }}
                />
              </IconButton>
            </GridLayoutItem>
            <GridLayoutItem>
              <hr className="mt-0" />
            </GridLayoutItem>
            <GridLayoutItem>
              <Typography.p>
                Are you sure you want to delete this Attachment?
              </Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="error"
                closeDialogFunction={closeAttachementDeleteOpen}
              />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() => handleDeleteAction(AttachementID)}
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>
    </>
  );
};

export default CreateToDo;
